import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button, GridItem, Select, Typography } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { SelectProps } from "@insite/mobius/Select/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import ReportConfigurationModal from "@tKgpco/Components/MyAccount/Reports/ReportConfigurationModal";
import backOrderedItemsReportHandler from "@tKgpco/Handlers/CustomBackOrderedItems";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface Props {
    report: Report;
}

export type Report = {
    id: string;
    description: string;
    title: string;
    isConfigurable: boolean;
    type: string;
};

type ComponentProps = Props & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReportStyles {
    container?: GridContainerProps;
    reportDetailsGridItem?: GridItemProps;
    formatSelectionGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    button?: ButtonPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    reportTitle?: TypographyPresentationProps;
}

export const reportStyles: ReportStyles = {
    container: {
        css: css`
            flex-direction: column;
        `,
    },
    reportDetailsGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            flex-direction: column;
        `,
    },
    formatSelectionGridItem: {
        width: [12, 8, 2, 2, 2],
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    buttonGridItem: {
        width: [12, 4, 2, 2, 2],
        css: css`
            justify-content: right;
        `,
    },
    button: {
        buttonType: "outline",
        variant: "secondary",
        mergeCss: true,
        css: css`
            [class^="IconWrapper"] {
                color: ${getColor("primary")};
            }
        `,
    },
    reportTitle: {
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
        `,
    },
};
const mapStateToProps = (state: ApplicationState) => ({
    settings: state.context.settings
});

const mapDispatchToProps = {
    backOrderedItemsReportHandler,
};

const styles = reportStyles;
const dropDownOption = [{
    key: "PDF",
    value: "pdf"
},{
    key: "Excel",
    value: "excel"
}]
const Report: FC<ComponentProps> = ({ report, backOrderedItemsReportHandler, settings }) => {
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [reportFormat, setReportFormat] = useState({
        purchaseHistory: "pdf",
        backOrderedItem: "pdf"
    });
    const [configurationModalIsOpen, setConfigurationModalIsOpen] = useState(false);
    const { properties } = settings;
    const toasterContext = React.useContext(ToasterContext);
    const handleSelect = (e: any, type: string) => {
        let backOrderedItem = reportFormat.backOrderedItem;
        let purchaseHistory = reportFormat.purchaseHistory;

        if(type === "backOrderedItem") {
            backOrderedItem = e.target.value;
        } else {
            purchaseHistory = e.target.value;
        }

        setReportFormat({
            purchaseHistory,
            backOrderedItem
        });
    };

    const getReport = (type: string) => {
        setIsButtonLoading(true);
        if (type === "backOrderedItem") {
            backOrderedItemReport();
        }
    };

    const backOrderedItemReport = () => {
        backOrderedItemsReportHandler({
            reportFormat: reportFormat.backOrderedItem,
            onSuccess: () => {
                setIsButtonLoading(false);
                toasterContext.addToast({ body: siteMessage("Report_SendMail"), messageType: "success" });
            },
            onError: () => {
                setIsButtonLoading(false);
            },
        });
    };

    const openPopup = () => {
        setConfigurationModalIsOpen(true);
    };

    const closePopup = () => {
        setConfigurationModalIsOpen(false);
    };

    useEffect(() => {
        const backOrderedItem = dropDownOption.filter((option) => properties.backOrderedItemsReportsType === "BOTH" ? true : properties.backOrderedItemsReportsType.toLowerCase() === option.key.toLowerCase())[0].value;
        const purchaseHistory = dropDownOption.filter((option) => properties.purchaseHistoryReportsType === "BOTH" ? true : properties.purchaseHistoryReportsType.toLowerCase() === option.key.toLowerCase())[0].value;

        setReportFormat({
            backOrderedItem,
            purchaseHistory
        });
    },[properties]);

    return (
        <GridContainer key={report.id} {...styles.container}>
            <GridItem {...styles.reportDetailsGridItem}>
                <Typography as="h2" {...styles.reportTitle}>
                    {report.title}
                </Typography>
                <Typography as="p">{report.description}</Typography>
            </GridItem>
            <GridItem {...styles.formatSelectionGridItem}>
                <Select value={report.type === "backOrderedItem"? reportFormat.backOrderedItem: reportFormat.purchaseHistory} onChange={(e) => handleSelect(e, report.type)}>
                    {
                        dropDownOption.filter(option => {
                            if(report.type === "backOrderedItem") {
                                return properties.backOrderedItemsReportsType === "BOTH" ? true : properties.backOrderedItemsReportsType.toLowerCase() === option.key.toLowerCase()
                            }
                            if(report.type === "purchaseHistory") {
                                return properties.purchaseHistoryReportsType === "BOTH" ? true : properties.purchaseHistoryReportsType.toLowerCase() === option.key.toLowerCase()
                            }
                            return true;
                        }).map((option, i) => {
                            return (
                                <option key={i} value={option.value}>{translate(option.key)}</option>
                            )
                        })
                    }
                </Select>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button
                    {...styles.button}
                    icon={{ src: isButtonLoading ? LoadingSpinner : "", position: "right" }}
                    onClick={() => (report.isConfigurable ? openPopup() : getReport(report.type))}
                >
                    {translate("Run Report")}
                </Button>
            </GridItem>
            <ReportConfigurationModal
                closePopup={closePopup}
                configurationModalIsOpen={configurationModalIsOpen}
                reportFormat={reportFormat.purchaseHistory}
            />
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Report);
